import React, { useState, useRef, useEffect } from 'react';
import './ProjectVideos.css';
import { FormattedMessage } from 'react-intl';
import HeaderPage from '../../components/Header/HeaderPage'; // Import the header

const ProjectVideos = () => {
    const [activeProject, setActiveProject] = useState(1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isThumbnailHovered, setIsThumbnailHovered] = useState(false);
    const videoRef = useRef(null);
    const thumbnailRef = useRef(null);
    
    const projects = [
        {
            id: 1,
            title: 'Sugar Bag Conveyor Counting System',
            category: 'Computer Vision & AI',
            shortDescription: 'YOLO & OpenCV based real-time counting system',
            description: 'Real-time object detection and counting system for industrial conveyor belts using YOLO and OpenCV',
            longDescription: 'This advanced computer vision system automates the counting of sugar bags on industrial conveyor belts. Using YOLO-based object detection with OpenCV, it accurately detects, tracks, and counts bags in real-time while filtering out background interference, stacked bags, and partial detections. The system implements ROI (Region of Interest) zones and size thresholds to ensure only fully visible bags crossing the virtual counting line are counted.',
            technologies: ['Python', 'OpenCV', 'YOLOv5', 'PyTorch', 'NumPy', 'TensorFlow', 'Computer Vision'],
            features: [
                'Real-time bag detection and counting',
                'ROI-based filtering for accuracy',
                'Background and stacked bag elimination',
                'Virtual counting line implementation',
                'Industrial environment optimized',
                'Automated reporting and logging'
            ],
            videoSource: '/ResultVideo.mp4',
            thumbnail: '/project-thumbnail.png',
            isLocal: true,
            githubLink: '#',
            liveDemo: '#',
            youtubeChannel: 'https://www.youtube.com/@codewithbuntu',
            stats: {
                accuracy: '98.5%',
                speed: '30 FPS',
                environment: 'Industrial'
            },
            achievements: [
                'Reduced manual counting errors by 95%',
                'Processes 30 frames per second',
                'Works in varying lighting conditions'
            ]
        },
        {
            id: 2,
            title: 'E-commerce Analytics Dashboard',
            category: 'Full Stack & Data Visualization',
            shortDescription: 'Real-time sales analytics platform',
            description: 'Interactive dashboard with real-time sales analytics and customer insights',
            longDescription: 'A comprehensive analytics platform that processes e-commerce data in real-time, providing insights into sales trends, customer behavior, and inventory management.',
            technologies: ['React', 'Node.js', 'MongoDB', 'D3.js', 'Express', 'Chart.js'],
            features: [
                'Real-time data visualization',
                'Customer segmentation',
                'Sales forecasting',
                'Inventory tracking',
                'Mobile responsive',
                'Export functionality'
            ],
            thumbnail: '/ecommerce-thumbnail.png',
            youtubeId: 'abc123def456',
            isLocal: false,
            githubLink: '#',
            liveDemo: '#',
            youtubeChannel: 'https://www.youtube.com/@codewithbuntu',
            stats: {
                accuracy: '95%',
                speed: 'Real-time',
                environment: 'Web'
            }
        },
        {
            id: 3,
            title: 'Smart Home Automation',
            category: 'IoT & Mobile Development',
            shortDescription: 'AI-powered home automation system',
            description: 'Mobile-controlled home automation system with AI-powered energy optimization',
            longDescription: 'An IoT-based smart home system that allows users to control appliances remotely while using AI to optimize energy consumption based on usage patterns.',
            technologies: ['React Native', 'Python', 'MQTT', 'TensorFlow Lite', 'Firebase', 'IoT'],
            features: [
                'Voice control integration',
                'Energy consumption analytics',
                'Automated scheduling',
                'Remote monitoring',
                'Real-time alerts',
                'Energy optimization'
            ],
            thumbnail: '/smarthome-thumbnail.png',
            youtubeId: 'xyz789ghi012',
            isLocal: false,
            githubLink: '#',
            liveDemo: '#',
            youtubeChannel: 'https://www.youtube.com/@codewithbuntu',
            stats: {
                accuracy: '97%',
                speed: 'Instant',
                environment: 'Mobile'
            }
        }
    ];

    const activeProjectData = projects.find(project => project.id === activeProject);

    const togglePlayPause = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };

    const handleVideoEnd = () => {
        setIsPlaying(false);
    };

    const handleVideoPlay = () => {
        setIsPlaying(true);
    };

    const handleVideoPause = () => {
        setIsPlaying(false);
    };

    const handleThumbnailHover = (hoverState) => {
        setIsThumbnailHovered(hoverState);
    };

    // Effect to handle video time updates for thumbnail replacement
    useEffect(() => {
        const videoElement = videoRef.current;
        
        const handleTimeUpdate = () => {
            if (videoElement && !isPlaying && videoElement.currentTime > 0) {
                // When paused, show the current frame as thumbnail
                const canvas = document.createElement('canvas');
                canvas.width = videoElement.videoWidth;
                canvas.height = videoElement.videoHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
                const dataURL = canvas.toDataURL();
                
                if (thumbnailRef.current) {
                    thumbnailRef.current.style.backgroundImage = `url(${dataURL})`;
                }
            }
        };

        if (videoElement) {
            videoElement.addEventListener('timeupdate', handleTimeUpdate);
        }

        return () => {
            if (videoElement) {
                videoElement.removeEventListener('timeupdate', handleTimeUpdate);
            }
        };
    }, [isPlaying]);

    return (
        <>
            {/* Add Header Page */}
            <HeaderPage />
            
            <div className="project-videos-page">
                {/* Enhanced Hero Section with YouTube Channel */}
                <section className="videos-hero">
                    <div className="container">
                        <div className="hero-content">
                            <div className="hero-header">
                                <h1 className="page-title">
                                    <FormattedMessage
                                        id="projectVideos.title"
                                        defaultMessage="Project Videos & Demos"
                                    />
                                </h1>
                                <div className="youtube-channel-link">
                                    <a 
                                        href="https://www.youtube.com/@codewithbuntu" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="yt-channel-btn"
                                    >
                                        <span className="yt-icon">▶️</span>
                                        <span className="yt-text">Visit My YouTube Channel</span>
                                    </a>
                                </div>
                            </div>
                            <p className="page-subtitle">
                                <FormattedMessage
                                    id="projectVideos.subtitle"
                                    defaultMessage="Watch detailed demonstrations and technical walkthroughs of my projects"
                                />
                            </p>
                            <div className="hero-stats">
                                <div className="stat-item">
                                    <div className="stat-number">3+</div>
                                    <div className="stat-label">Projects</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-number">98%</div>
                                    <div className="stat-label">Average Accuracy</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-number">24/7</div>
                                    <div className="stat-label">Real-time Operation</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-number">1000+</div>
                                    <div className="stat-label">Lines of Code</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Main Content */}
                <div className="container">
                    <div className="project-videos-layout">
                        {/* Project List Sidebar */}
                        <div className="project-sidebar">
                            <div className="sidebar-header">
                                <h3>Featured Projects</h3>
                                <p>Select a project to view details</p>
                            </div>
                            <div className="project-list">
                                {projects.map(project => (
                                    <div 
                                        key={project.id} 
                                        className={`project-list-item ${activeProject === project.id ? 'active' : ''}`}
                                        onClick={() => {
                                            setActiveProject(project.id);
                                            setIsPlaying(false);
                                        }}
                                    >
                                        <div className="project-icon">
                                            {project.id === 1 && '🏭'}
                                            {project.id === 2 && '📊'}
                                            {project.id === 3 && '🏠'}
                                        </div>
                                        <div className="project-list-content">
                                            <h4>{project.title}</h4>
                                            <span className="project-category">{project.category}</span>
                                            <p className="project-short-desc">{project.shortDescription}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* YouTube Channel in Sidebar */}
                            <div className="sidebar-yt-channel">
                                <h4>📺 My YouTube Channel</h4>
                                <p>Subscribe for more project tutorials and demos</p>
                                <a 
                                    href="https://www.youtube.com/@codewithbuntu" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="sidebar-yt-btn"
                                >
                                    Visit Channel
                                </a>
                            </div>
                        </div>

                        {/* Project Details */}
                        <div className="project-details">
                            <div className="project-video-section">
                                {/* Video Player with Thumbnail Overlay */}
                                <div className="video-player-container">
                                    <div className="video-wrapper">
                                        {activeProjectData.isLocal ? (
                                            <div className="local-video-player">
                                                <video 
                                                    ref={videoRef}
                                                    className="project-video"
                                                    controls
                                                    onEnded={handleVideoEnd}
                                                    onPlay={handleVideoPlay}
                                                    onPause={handleVideoPause}
                                                    poster={activeProjectData.thumbnail}
                                                >
                                                    <source src={activeProjectData.videoSource} type="video/mp4" />
                                                    Your browser does not support the video tag.
                                                </video>
                                                
                                                {/* Custom Play/Pause Overlay with Zoom Thumbnail */}
                                                {!isPlaying && (
                                                    <div 
                                                        className="video-overlay" 
                                                        onClick={togglePlayPause}
                                                        onMouseEnter={() => handleThumbnailHover(true)}
                                                        onMouseLeave={() => handleThumbnailHover(false)}
                                                    >
                                                        <div 
                                                            className={`thumbnail-container ${isThumbnailHovered ? 'zoom-out' : ''}`}
                                                            ref={thumbnailRef}
                                                            style={{
                                                                backgroundImage: `url(${activeProjectData.thumbnail})`
                                                            }}
                                                        >
                                                            <div className="thumbnail-overlay">
                                                                <div className="play-button-overlay">
                                                                    <div className="play-button-circle">
                                                                        <span className="play-icon">▶</span>
                                                                    </div>
                                                                    <span className="play-text">Click to Play Demo</span>
                                                                    <span className="play-subtext">Watch the project in action</span>
                                                                </div>
                                                                <div className="thumbnail-info">
                                                                    <div className="thumbnail-badge">DEMO</div>
                                                                    <div className="thumbnail-title">{activeProjectData.title}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Custom Controls */}
                                                <div className="custom-video-controls">
                                                    <button 
                                                        className="custom-control-btn play-pause-btn"
                                                        onClick={togglePlayPause}
                                                    >
                                                        {isPlaying ? '⏸️' : '▶️'}
                                                    </button>
                                                    <div className="video-info-overlay">
                                                        <span className="video-title-overlay">{activeProjectData.title}</span>
                                                        <span className="video-duration">03:45</span>
                                                    </div>
                                                    <button 
                                                        className="custom-control-btn fullscreen-btn"
                                                        onClick={() => videoRef.current?.requestFullscreen()}
                                                    >
                                                        ⛶
                                                    </button>
                                                    <button 
                                                        className="custom-control-btn volume-btn"
                                                        onClick={() => {
                                                            if (videoRef.current) {
                                                                videoRef.current.muted = !videoRef.current.muted;
                                                            }
                                                        }}
                                                    >
                                                        🔈
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="youtube-video">
                                                <div className="youtube-placeholder">
                                                    <div className="youtube-thumbnail">
                                                        <img 
                                                            src={activeProjectData.thumbnail} 
                                                            alt={activeProjectData.title}
                                                            className="youtube-thumbnail-img"
                                                        />
                                                        <div className="youtube-play-btn" onClick={() => window.open(`https://www.youtube.com/watch?v=${activeProjectData.youtubeId}`, '_blank')}>
                                                            <div className="youtube-icon">▶</div>
                                                        </div>
                                                    </div>
                                                    <div className="youtube-info">
                                                        <h4>YouTube Demo Available</h4>
                                                        <p>Click to watch on YouTube</p>
                                                        <button 
                                                            className="watch-youtube-btn"
                                                            onClick={() => window.open(`https://www.youtube.com/watch?v=${activeProjectData.youtubeId}`, '_blank')}
                                                        >
                                                            Watch on YouTube
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Project Information */}
                                <div className="project-info">
                                    <div className="project-header">
                                        <div className="project-title-section">
                                            <div className="project-badge">Featured Project</div>
                                            <h2>{activeProjectData.title}</h2>
                                            <div className="project-category-tags">
                                                <span className="project-category-badge">{activeProjectData.category}</span>
                                                {activeProjectData.technologies.slice(0, 3).map((tech, index) => (
                                                    <span key={index} className="tech-tag-mini">{tech}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="project-actions">
                                            <a 
                                                href={activeProjectData.githubLink} 
                                                className="action-btn github-btn"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <span className="btn-icon">🐙</span>
                                                View Code
                                            </a>
                                            <a 
                                                href={activeProjectData.youtubeChannel} 
                                                className="action-btn youtube-btn"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <span className="btn-icon">▶️</span>
                                                YouTube
                                            </a>
                                        </div>
                                    </div>

                                    <div className="project-stats-container">
                                        {Object.entries(activeProjectData.stats).map(([key, value]) => (
                                            <div key={key} className="stat-card">
                                                <div className="stat-value">{value}</div>
                                                <div className="stat-label">{key.charAt(0).toUpperCase() + key.slice(1)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="project-description">
                                        <h3>📋 Project Overview</h3>
                                        <p>{activeProjectData.longDescription}</p>
                                        
                                        {activeProject === 1 && (
                                            <div className="project-highlights">
                                                <h4>🌟 Key Achievements:</h4>
                                                <ul>
                                                    {activeProjectData.achievements?.map((achievement, index) => (
                                                        <li key={index}>
                                                            <span className="highlight-icon">✅</span>
                                                            {achievement}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    <div className="project-details-grid">
                                        <div className="detail-card">
                                            <h4>🛠️ Technologies Used</h4>
                                            <div className="tech-stack">
                                                {activeProjectData.technologies.map((tech, index) => (
                                                    <span key={index} className="tech-tag">{tech}</span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="detail-card">
                                            <h4>✨ Key Features</h4>
                                            <ul className="feature-list">
                                                {activeProjectData.features.map((feature, index) => (
                                                    <li key={index}>
                                                        <span className="feature-icon">✓</span>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="detail-card">
                                            <h4>🔗 Project Links</h4>
                                            <div className="project-links">
                                                <a 
                                                    href={activeProjectData.githubLink} 
                                                    className="project-link-btn github"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <span className="link-icon">🐙</span>
                                                    GitHub Repository
                                                </a>
                                                <a 
                                                    href={activeProjectData.liveDemo} 
                                                    className="project-link-btn demo"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <span className="link-icon">🚀</span>
                                                    Live Demo
                                                </a>
                                                <a 
                                                    href={activeProjectData.youtubeChannel} 
                                                    className="project-link-btn youtube"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <span className="link-icon">▶️</span>
                                                    YouTube Channel
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Computer Vision Features for Sugar Project */}
                                    {activeProject === 1 && (
                                        <div className="special-features">
                                            <h3>🔬 Computer Vision Features</h3>
                                            <div className="cv-features">
                                                <div className="cv-feature">
                                                    <div className="cv-icon">🎯</div>
                                                    <div>
                                                        <h5>YOLO Object Detection</h5>
                                                        <p>Real-time detection using YOLOv5 for high accuracy</p>
                                                    </div>
                                                </div>
                                                <div className="cv-feature">
                                                    <div className="cv-icon">📐</div>
                                                    <div>
                                                        <h5>ROI Filtering</h5>
                                                        <p>Region of Interest optimization to eliminate false detections</p>
                                                    </div>
                                                </div>
                                                <div className="cv-feature">
                                                    <div className="cv-icon">⚡</div>
                                                    <div>
                                                        <h5>Real-time Processing</h5>
                                                        <p>30 FPS processing for immediate counting results</p>
                                                    </div>
                                                </div>
                                                <div className="cv-feature">
                                                    <div className="cv-icon">🏭</div>
                                                    <div>
                                                        <h5>Industrial Grade</h5>
                                                        <p>Optimized for factory lighting and environmental conditions</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Call to Action */}
                                    <div className="cta-section">
                                        <h3>📢 Want to See More?</h3>
                                        <p>Check out my YouTube channel for more project tutorials, code walkthroughs, and tech insights.</p>
                                        <a 
                                            href="https://www.youtube.com/@codewithbuntu" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="cta-btn"
                                        >
                                            <span className="cta-icon">▶️</span>
                                            Subscribe to My YouTube Channel
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProjectVideos;